import React from 'react';
import { Place, Person } from '../../../types/results.types';
import DecisionMakerPreviewStack from '../shared/DecisionMakerPreviewStack';

interface ContactCellProps {
  place: Place;
  hints?: boolean;
  publicRecordsOnly?: boolean;
}

const SUCCESS = '#059669';
const DARK = '#1C1B22';

const Avatar: React.FC<{ initials: string; size?: number; bg?: string }> = ({
  initials,
  size = 30,
  bg = DARK,
}) => (
  <div
    className="flex-shrink-0 flex items-center justify-center rounded-full font-bold select-none text-white"
    style={{ width: size, height: size, background: bg, fontSize: size * 0.34 }}
  >
    {initials}
  </div>
);

function getInitials(person: Person): string {
  const first = person.corrected_first_name || person.first_name || '';
  const last = person.corrected_last_name || person.last_name || '';
  return ((first[0] || '') + (last[0] || '')).toUpperCase() || '?';
}

function getFullName(person: Person): string {
  const first = person.corrected_first_name || person.first_name || '';
  const last = person.corrected_last_name || person.last_name || '';
  return [first, last].filter(Boolean).join(' ');
}

function getRole(person: Person): string {
  return person.corrected_role || person.role || '';
}

function getVerifiedEmail(person: Person) {
  return person.emails?.find(e => e.verification_status === 'GUARANTEED') ?? null;
}

function isVerified(person: Person): boolean {
  return !!getVerifiedEmail(person);
}

function avatarBg(person: Person, locked: boolean): string {
  if (locked) return '#C4C9D4';
  if (isVerified(person)) return SUCCESS;
  if ((person.emails?.length ?? 0) > 0) return DARK;
  return '#9CA3AF';
}

const LockBadge = () => (
  <div
    className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white flex items-center justify-center"
    style={{ background: '#9CA3AF' }}
  >
    <svg width="7" height="7" viewBox="0 0 8 8" fill="none">
      <rect x="1.5" y="3.5" width="5" height="4" rx="1" stroke="white" strokeWidth="1.2" fill="none" />
      <path d="M2.5 3.5V2.5a1.5 1.5 0 013 0v1" stroke="white" strokeWidth="1.2" strokeLinecap="round" fill="none" />
    </svg>
  </div>
);

const CheckBadge = () => (
  <div
    className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white flex items-center justify-center"
    style={{ background: SUCCESS }}
  >
    <svg width="6" height="6" viewBox="0 0 8 8" fill="none">
      <path d="M1.5 4L3 5.5L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

const DetailMode: React.FC<{ place: Place; hints: boolean; publicRecordsOnly?: boolean }> = ({
  place,
  hints,
  publicRecordsOnly = false,
}) => {
  const people = place.people ?? [];
  const primary = people[0];
  const extraCount = people.length - 1;
  const lockedCount = people.filter(p => !p.is_unlocked).length;
  const isMulti = people.length >= 2;

  const locked = !publicRecordsOnly && !primary.is_unlocked;
  const verified = isVerified(primary);
  const uniqueRoles = isMulti
    ? [...new Set(people.map(p => getRole(p)).filter(Boolean))].slice(0, 2)
    : [];
  const rolesLabel = isMulti
    ? uniqueRoles.join(', ') + (people.length > uniqueRoles.length ? ' + more' : '')
    : '';

  return (
    <div className="flex flex-col gap-1.5">
      {/* Person row */}
      <div className="flex items-center gap-2.5">
        <div className="relative flex-shrink-0">
          <Avatar initials={getInitials(primary)} size={30} bg={avatarBg(primary, locked)} />
          {!publicRecordsOnly && locked && <LockBadge />}
          {!publicRecordsOnly && !locked && verified && <CheckBadge />}
        </div>
        <div className="flex-1 min-w-0">
          {isMulti ? (
            <>
              <div className="text-[13px] font-semibold text-[#1C1B22]">{people.length} people</div>
              <div className="text-[10.5px] text-[#6B7280] mt-0.5 truncate max-w-[140px]">{rolesLabel}</div>
            </>
          ) : (
            <>
              <div className="text-[13px] font-semibold text-[#1C1B22] truncate">{getFullName(primary)}</div>
              <div className="text-[11px] text-[#6B7280] mt-0.5">{getRole(primary)}</div>
            </>
          )}
        </div>
      </div>

      {/* Extra contacts — only in single-person mode */}
      {!isMulti && extraCount > 0 && (
        <div className="pl-10 flex items-center gap-1 text-[11px] text-[#9CA3AF]">
          <span>+{extraCount} more</span>
          {lockedCount > 0 && (
            <span className="font-semibold text-[#B45309]">· {lockedCount} not yet searched</span>
          )}
        </div>
      )}
    </div>
  );
};

const StackMode: React.FC<{ place: Place; hints: boolean }> = ({ place, hints }) => {
  const people = place.people ?? [];
  const verifiedCount = people.filter(p => isVerified(p)).length;
  const foundCount = people.filter(p => p.is_unlocked && (p.emails?.length ?? 0) > 0 && !isVerified(p)).length;
  const notSearchedCount = people.filter(p => p.email_access_state === 'not_searched').length;
  const remainingCount = people.filter(p => p.email_access_state === 'gated_upgrade').length;

  return (
    <div className="flex flex-col gap-1.5">
      <DecisionMakerPreviewStack people={people} />

      {/* Status summary */}
      {hints && (
        <div className="flex gap-3 flex-wrap">
          {verifiedCount > 0 && (
            <span className="flex items-center gap-1 text-[10.5px] font-semibold" style={{ color: SUCCESS }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: SUCCESS }} />
              {verifiedCount} verified
            </span>
          )}
          {foundCount > 0 && (
            <span className="flex items-center gap-1 text-[10.5px] font-semibold" style={{ color: DARK }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: DARK }} />
              {foundCount} found
            </span>
          )}
          {notSearchedCount > 0 && (
            <span className="flex items-center gap-1 text-[10.5px]" style={{ color: '#9CA3AF' }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block bg-[#D1D5DB]" />
              {notSearchedCount} not searched
            </span>
          )}
          {remainingCount > 0 && (
            <span className="flex items-center gap-1 text-[10.5px]" style={{ color: '#9CA3AF' }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block bg-[#D1D5DB]" />
              {remainingCount} remaining
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const ContactCell: React.FC<ContactCellProps> = ({ place, hints = true, publicRecordsOnly = false }) => {
  const people = place.people ?? [];

  if (people.length === 0) {
    if (publicRecordsOnly) {
      return <span className="text-[12px] text-[#C4C7CC]" aria-label="No decision maker found">—</span>;
    }
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-[12px] font-medium text-[#9CA3AF]">No decision maker found</span>
      </div>
    );
  }

  if (people.length >= 4 && !publicRecordsOnly) {
    return <StackMode place={place} hints={hints} />;
  }

  return <DetailMode place={place} hints={hints} publicRecordsOnly={publicRecordsOnly} />;
};

export default ContactCell;
